import { Injectable } from '@angular/core';
import { Observable, of, forkJoin } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { PrTimeToFirstReviewResult, CalculationLogic, DEFAULT_CALCULATION_LOGIC } from '../models/team-metrics';
import { TeamMetricsService } from './team-metrics.service';
import { OwnershipEntry } from '../models/ownership-entry';
import { PlatformPortalApiService } from './platform-portal-api.service';
import { PullRequestFirstReview, PullRequestFirstReviewResponse } from '../models/pull-request-first-review';

/** Review durations (hours) and the number of non-draft PRs they were drawn from. */
interface ReviewDurationSample {
  durations: number[];
  totalPrs: number;
  failed: boolean;
}

const EMPTY_SAMPLE: ReviewDurationSample = { durations: [], totalPrs: 0, failed: false };

@Injectable({
  providedIn: 'root'
})
/**
 * Service responsible for calculating the "PR Time to First Review" metric.
 * Pull requests are read from GitHub through platform-portal-api's first-review endpoint
 * (Azure DevOps repositories are deprecated), then summarized as Mean, Median or IQR.
 */
export class PrTimeToReviewCalculatorService {
  /** Must not exceed the API's per-request repository limit. */
  private readonly maxGitHubRepositoriesPerRequest = 200;

  /**
   * Constructs the PrTimeToReviewCalculatorService.
   * @param teamMetricsService Service for shared team metrics functionalities and utilities.
   * @param platformPortalApiService Backend API, used for GitHub pull request review data.
   */
  constructor(
    private readonly teamMetricsService: TeamMetricsService,
    private readonly platformPortalApiService: PlatformPortalApiService
  ) { }

  /**
   * Calculates the PR Time to First Review metric for a given team and period.
   * Optionally includes comparison data from the previous equivalent period.
   * @param teamName The name of the team for which to calculate the metric.
   * @param startDate The start date of the calculation period in ISO format (UTC).
   * @param endDate The end date of the calculation period in ISO format (UTC).
   * @param org The ADO organization name. Not used for this metric; kept so callers share one signature with the other metrics.
   * @param calculationLogic The logic to use for calculation (e.g., 'Mean', 'Median', 'IQR'). Defaults to `DEFAULT_CALCULATION_LOGIC`.
   * @param includeComparison Flag indicating whether to include comparison data. Defaults to true.
   * @returns An Observable emitting the `PrTimeToFirstReviewResult`.
   */
  public calculate(
    teamName: string,
    startDate: string,
    endDate: string,
    org: string,
    calculationLogic: CalculationLogic = DEFAULT_CALCULATION_LOGIC,
    includeComparison: boolean = true
  ): Observable<PrTimeToFirstReviewResult> {
    if (includeComparison) {
      return this.teamMetricsService.addComparisonToMetric(
        (team, start, end, calcLogic) => this.calculateSinglePeriod(team, start, end, calcLogic ?? DEFAULT_CALCULATION_LOGIC),
        teamName,
        startDate,
        endDate,
        true,
        'prTime',
        calculationLogic
      );
    }
    return this.calculateSinglePeriod(teamName, startDate, endDate, calculationLogic);
  }

  /**
   * Calculates PR Time to First Review for a single period: reads the team's GitHub-hosted
   * repositories' pull requests and first reviews from the API and builds the result.
   * @param teamName The name of the team.
   * @param startDate The start date of the period in ISO format (UTC).
   * @param endDate The end date of the period in ISO format (UTC).
   * @param calculationLogic The calculation logic to apply (Mean, Median, IQR).
   * @returns An Observable emitting the `PrTimeToFirstReviewResult` for the single period.
   */
  private calculateSinglePeriod(
    teamName: string,
    startDate: string,
    endDate: string,
    calculationLogic: CalculationLogic
  ): Observable<PrTimeToFirstReviewResult> {
    return this.teamMetricsService.ensureOwnershipDataLoaded().pipe(
      switchMap(ownershipEntries => {
        const teamOwnershipEntries = ownershipEntries.filter(entry => entry.team === teamName);
        if (teamOwnershipEntries.length === 0) {
          return of(this.createEmptyPrTimeToFirstReviewResult('Team not found', startDate, endDate, calculationLogic, 0));
        }

        const repositories = teamOwnershipEntries.filter(entry => this.isGitHubHosted(entry)).map(entry => entry.repository);
        return this.fetchGitHubReviewDurations(repositories, startDate, endDate).pipe(
          map(sample => this.toResult(sample, startDate, endDate, calculationLogic))
        );
      })
    );
  }

  /**
   * Builds the result for a period from the GitHub sample.
   * @param sample Review durations and PR count for the period.
   * @param startDate The start date of the period in ISO format (UTC).
   * @param endDate The end date of the period in ISO format (UTC).
   * @param calculationLogic The calculation logic to apply (Mean, Median, IQR).
   * @returns The `PrTimeToFirstReviewResult` for the period.
   */
  private toResult(
    sample: ReviewDurationSample,
    startDate: string,
    endDate: string,
    calculationLogic: CalculationLogic
  ): PrTimeToFirstReviewResult {
    if (sample.totalPrs === 0) {
      const subtitle = sample.failed ? 'Unable to load GitHub PR data' : 'No PRs found in period';
      return this.createEmptyPrTimeToFirstReviewResult(subtitle, startDate, endDate, calculationLogic, 0);
    }
    return this.buildPrTimeToFirstReviewResult(sample.durations, sample.durations.length, sample.totalPrs, startDate, endDate, calculationLogic);
  }

  /**
   * Whether an ownership entry's repository is hosted in GitHub, per its `location` field.
   * Entries still marked as Azure DevOps-hosted (deprecated) are skipped.
   * @param entry The ownership entry.
   * @returns `true` for GitHub-hosted repositories.
   */
  private isGitHubHosted(entry: OwnershipEntry): boolean {
    return entry.location?.toLowerCase() === 'github';
  }

  /**
   * Fetches review durations for GitHub-hosted repositories from platform-portal-api in batches.
   * The owner is omitted so the API uses its configured GitHub organization.
   * A failed request is logged and reported as `failed` so the metric can still render with the remaining data.
   * @param repositories GitHub repository names.
   * @param startDate The start date of the period in ISO format (UTC).
   * @param endDate The end date of the period in ISO format (UTC).
   * @returns An Observable emitting the GitHub sample.
   */
  private fetchGitHubReviewDurations(repositories: string[], startDate: string, endDate: string): Observable<ReviewDurationSample> {
    if (repositories.length === 0) {
      return of(EMPTY_SAMPLE);
    }

    const requests = this.toBatches(repositories).map(batch =>
      this.platformPortalApiService.getPullRequestFirstReviews({ repositories: batch, startDate, endDate }).pipe(
        map(response => this.toGitHubSample(response)),
        catchError(error => {
          console.error('[PRT Calc Service] Failed to load GitHub PR reviews:', error);
          return of({ ...EMPTY_SAMPLE, failed: true });
        })
      )
    );

    return forkJoin(requests).pipe(
      map(samples => ({
        durations: samples.flatMap(sample => sample.durations),
        totalPrs: samples.reduce((total, sample) => total + sample.totalPrs, 0),
        failed: samples.some(sample => sample.failed)
      }))
    );
  }

  /**
   * Splits repositories into API-sized batches.
   * @param repositories GitHub repository names.
   * @returns Batches of at most `maxGitHubRepositoriesPerRequest` repositories.
   */
  private toBatches(repositories: string[]): string[][] {
    const batches: string[][] = [];
    for (let i = 0; i < repositories.length; i += this.maxGitHubRepositoriesPerRequest) {
      batches.push(repositories.slice(i, i + this.maxGitHubRepositoriesPerRequest));
    }
    return batches;
  }

  /**
   * Converts the API response into review durations, logging any repositories the API could not read.
   * @param response The GitHub first-review response.
   * @returns The GitHub sample for the response.
   */
  private toGitHubSample(response: PullRequestFirstReviewResponse): ReviewDurationSample {
    if (response.errors?.length) {
      console.warn('[PRT Calc Service] GitHub repositories could not be read:', response.errors);
    }
    if (response.truncatedRepositories?.length) {
      console.warn('[PRT Calc Service] GitHub PR scan truncated for:', response.truncatedRepositories);
    }

    const pullRequests = response.pullRequests ?? [];
    const durations = pullRequests
      .filter((pr: PullRequestFirstReview) => !!pr.firstReviewAt)
      .map((pr: PullRequestFirstReview) => {
        const durationHours = (new Date(pr.firstReviewAt!).getTime() - new Date(pr.createdAt).getTime()) / (1000 * 60 * 60);
        console.log(`[PRT Calc Service] PR Review Found - Repo: ${pr.repository}, PR #${pr.number}, Duration: ${durationHours.toFixed(2)}h`);
        return durationHours;
      });

    return { durations, totalPrs: pullRequests.length, failed: false };
  }

  /**
   * Builds the `PrTimeToFirstReviewResult` object from calculated durations and other data.
   * @param durations An array of review durations in hours.
   * @param prsConsidered The number of PRs that had valid reviews.
   * @param totalPrsFetched The total number of PRs fetched for the period.
   * @param startDate The start date of the calculation period in ISO format (UTC).
   * @param endDate The end date of the calculation period in ISO format (UTC).
   * @param calculationLogic The calculation logic used (Mean, Median, IQR).
   * @returns A `PrTimeToFirstReviewResult` object.
   */
  public buildPrTimeToFirstReviewResult(
    durations: number[], 
    prsConsidered: number, 
    totalPrsFetched: number, 
    startDate: string, 
    endDate: string,
    calculationLogic: CalculationLogic
  ): PrTimeToFirstReviewResult {
    console.log(`[PRT Calc Service] Building Result - Period: ${this.teamMetricsService.formatDateRange(startDate, endDate)}, Calc: ${calculationLogic}`);
    console.log(`[PRT Calc Service] Durations (${durations.length}): [${durations.slice(0,10).map(d => d.toFixed(1)).join(', ')}${durations.length > 10 ? '...' : ''}]`);

    if (durations.length === 0) {
      const subtitle = totalPrsFetched > 0 
        ? `${prsConsidered} pull requests considered` 
        : 'No PRs found in period';
      return this.createEmptyPrTimeToFirstReviewResult(subtitle, startDate, endDate, calculationLogic, totalPrsFetched);
    }

    let value: number = 0;
    let displayValue: string;
    let q1DurationHours: number | undefined;
    let q3DurationHours: number | undefined;

    switch (calculationLogic) {
      case 'Mean': {
        const meanResult = this.getMeanPrTimeResult(durations);
        value = meanResult.value;
        displayValue = meanResult.displayValue;
        break;
      }
      case 'IQR': {
        const iqrResult = this.getIqrPrTimeResult(durations);
        value = iqrResult.value;
        displayValue = iqrResult.displayValue;
        q1DurationHours = iqrResult.q1;
        q3DurationHours = iqrResult.q3;
        break;
      }
      case 'Median':
      default: {
        const medianResult = this.getMedianPrTimeResult(durations);
        value = medianResult.value;
        displayValue = medianResult.displayValue;
        break;
      }
    }
    
    const outliers = durations.filter(d => d > 48).length;
    const subtitle = this.generatePrTimeSubtitle(durations.length, outliers);

    return {
      value,
      displayValue,
      subtitle,
      q1DurationHours,
      q3DurationHours,
      totalPrsConsidered: durations.length,
      totalPrsFetched: totalPrsFetched,
      period: this.teamMetricsService.formatDateRange(startDate, endDate),
      calculationMethodUsed: calculationLogic,
      rawDurationsHours: durations,
      startDateIso: startDate,
      endDateIso: endDate
    };
  }

  /**
   * Creates an empty `PrTimeToFirstReviewResult` object, used when no valid PRs or reviews are found.
   * @param subtitle A string describing why the result is empty (e.g., "No PRs found for team").
   * @param startDate The start date of the calculation period in ISO format (UTC).
   * @param endDate The end date of the calculation period in ISO format (UTC).
   * @param calculationLogic The calculation logic that was attempted.
   * @param totalPrsFetched The total number of PRs fetched for the period.
   * @returns An empty `PrTimeToFirstReviewResult` object.
   */
  private createEmptyPrTimeToFirstReviewResult(subtitle: string, startDate: string, endDate: string, calculationLogic: CalculationLogic, totalPrsFetched: number = 0): PrTimeToFirstReviewResult {
    return {
      value: 0, 
      displayValue: 'N/A', 
      subtitle: subtitle,
      totalPrsConsidered: 0, 
      totalPrsFetched: totalPrsFetched, 
      period: this.teamMetricsService.formatDateRange(startDate, endDate),
      calculationMethodUsed: calculationLogic,
      rawDurationsHours: [],
      startDateIso: startDate,
      endDateIso: endDate
    };
  }
  
  /**
   * Calculates the mean (average) PR time to first review.
   * @param durations An array of review durations in hours.
   * @returns An object containing the mean value and its display string.
   */
  private getMeanPrTimeResult(durations: number[]): { value: number; displayValue: string } {
    const meanVal = this.teamMetricsService.calculateMean(durations);
    if (meanVal !== null) {
      return { value: meanVal, displayValue: `${meanVal.toFixed(1)} hours` };
    }
    return { value: 0, displayValue: 'N/A' };
  }

  /**
   * Calculates the Interquartile Range (IQR) of PR time to first review.
   * @param durations An array of review durations in hours.
   * @returns An object containing the IQR value, its display string, and Q1 & Q3 values.
   */
  private getIqrPrTimeResult(durations: number[]): { value: number; displayValue: string; q1?: number; q3?: number } {
    const iqrVal = this.teamMetricsService.calculateIQR(durations);
    if (iqrVal !== null) {
      return { 
        value: iqrVal.iqr, 
        displayValue: `${iqrVal.iqr.toFixed(1)} hours`,
        q1: iqrVal.q1,
        q3: iqrVal.q3
      };
    }
    return { value: 0, displayValue: 'N/A' };
  }

  /**
   * Calculates the median PR time to first review.
   * @param durations An array of review durations in hours.
   * @returns An object containing the median value and its display string.
   */
  private getMedianPrTimeResult(durations: number[]): { value: number; displayValue: string } {
    const medianVal = this.teamMetricsService.calculateMedian(durations);
    if (medianVal !== null) {
      return { value: medianVal, displayValue: `${medianVal.toFixed(1)} hours` };
    }
    return { value: 0, displayValue: 'N/A' };
  }

  /**
   * Generates a subtitle string for the PR Time to First Review metric,
   * indicating the number of PRs considered and any outliers (reviews taking >48h).
   * @param durationsLength The number of PRs with valid reviews.
   * @param outliers The number of PRs where the first review took longer than 48 hours.
   * @returns A subtitle string.
   */
  private generatePrTimeSubtitle(durationsLength: number, outliers: number): string {
    const prString = `pull request${durationsLength === 1 ? '' : 's'}`;
    if (outliers === 0) {
      return `${durationsLength} ${prString}`;
    } else if (outliers === 1) {
      return `${durationsLength} ${prString} (1 took >48h)`;
    } else {
      return `${durationsLength} ${prString} (${outliers} took >48h)`;
    }
  }
}
